
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useCollections } from "@/contexts/collections";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ColorPicker from "@/components/common/ColorPicker";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Tier1Tracking } from "@/services/analytics/Tier1Tracking";

interface AddCollectionFormProps {
  onSuccess?: () => void;
  onComplete?: () => void;
}

const AddCollectionForm = ({ onSuccess, onComplete }: AddCollectionFormProps) => {
  const [name, setName] = useState("");
  const [color, setColor] = useState("#3B82F6");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  const { refreshCollections } = useCollections();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error("Collection name is required");
      return;
    }

    if (!user) {
      toast.error("You must be logged in to create a collection");
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase
        .from("collections")
        .insert({
          name: name.trim(),
          color,
          user_id: user.id,
          position: 0, // Add required position field
        })
        .select();

      if (error) {
        console.error("Error creating collection:", error);
        toast.error("Failed to create collection");
        return;
      }

      if (data && data.length > 0) {
        const newCollection = data[0];
        
        // Track collection creation with Tier 1 analytics
        Tier1Tracking.trackCollectionCreated(
          newCollection.id,
          newCollection.name,
          newCollection.color,
          user
        );

        toast.success("Collection created successfully");
        setName("");
        setColor("#3B82F6");
        refreshCollections();
        
        if (onSuccess) {
          onSuccess();
        }
        if (onComplete) {
          onComplete();
        }
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="collection-name">Collection Name</Label>
        <Input
          id="collection-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter collection name"
          required
        />
      </div>
      
      <div>
        <Label htmlFor="collection-color">Color</Label>
        <ColorPicker 
          value={color} 
          onChange={setColor}
        />
      </div>
      
      <Button 
        type="submit" 
        disabled={isSubmitting || !name.trim()}
        className="w-full"
      >
        {isSubmitting ? "Creating..." : "Create Collection"}
      </Button>
    </form>
  );
};

export default AddCollectionForm;
